import { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  borderPrimary,
  semiBoldText,
  primaryText,
  largeTextSize,
  smallTextSize,
  secondaryText,
  whiteBG,
  baseTextSize,
  mediumText,
} from '../../styling/stylingHelper';

interface RevenueItemQuantityEditModalProps {
  itemName: string;
  currentQuantity: number;
  onUpdateQuantity: (newQuantity: number) => Promise<void>;
  onClose: () => void;
}

const RevenueItemQuantityEditModal = ({
  itemName,
  currentQuantity,
  onUpdateQuantity,
  onClose,
}: RevenueItemQuantityEditModalProps) => {
  const [quantity, setQuantity] = useState<string>(currentQuantity.toString());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const newQuantity = parseFloat(quantity);

    if (isNaN(newQuantity) || newQuantity <= 0) {
      alert('Please enter a valid positive number');
      return;
    }

    setIsSubmitting(true);
    try {
      await onUpdateQuantity(newQuantity);
      onClose();
    } catch (error) {
      console.error('Failed to update quantity:', error);
      alert('Failed to update quantity');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className={cn(
          "bg-white dark:bg-background-dark rounded-xl shadow-xl w-full max-w-md border",
          borderPrimary
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className={cn(semiBoldText, largeTextSize, primaryText, "mb-1")}>
                Edit Revenue Item Quantity
              </h2>
              <p className={cn(smallTextSize, secondaryText)}>
                "{itemName}"
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Quantity Input */}
          <div className="mb-4">
            <label className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
              New Total Quantity
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              step="1"
              min="1"
              className={cn(
                "w-full p-3 border rounded-xl",
                borderPrimary,
                baseTextSize,
                primaryText,
                "focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-500"
              )}
              placeholder="Enter quantity"
              autoFocus
            />
            <p className={cn(smallTextSize, secondaryText, "mt-2")}>
              Current: {currentQuantity} items
            </p>
            <p className={cn(smallTextSize, secondaryText, "mt-1", "italic")}>
              Note: Quantities will be scaled across all orders and rounded to integers
            </p>
          </div>

          {/* Actions */}
          <div className="-mx-4">
            <div className="border-t border-borderPrimary dark:border-borderPrimary-dark w-full" />
            <div className="flex justify-end gap-3 pt-4 px-4">
              <button
                onClick={onClose}
                className={cn(
                  smallTextSize,
                  secondaryText,
                  semiBoldText,
                  "rounded-xl px-4 py-3",
                  whiteBG,
                  "border",
                  borderPrimary,
                  "hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark",
                  "flex-1"
                )}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={cn(
                  smallTextSize,
                  "text-white dark:text-white",
                  semiBoldText,
                  "bg-darkGreenColor dark:bg-darkGreenColor-dark rounded-xl px-4 py-3 border",
                  borderPrimary,
                  "hover:bg-darkGreenColor/95 dark:hover:bg-darkGreenColor-dark/95",
                  "flex-1",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isSubmitting ? 'Updating...' : 'Update Quantity'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueItemQuantityEditModal;
